import BaseModal from 'components/Modals/BaseModal';
import { Button } from 'react-bootstrap';

interface ForceUnsafeRootModalProps {
  show: boolean;
  /** Path the supervisor refused to accept. Renders verbatim in the modal copy. */
  rootDir: string;
  /** Server's error message, surfaced as supporting context under the headline. */
  serverMessage: string;
  /** User chose to retry with `forceUnsafeRoot: true`. */
  onConfirm: () => void;
  /** User backed out — parent re-surfaces the original 400 error on the form modal. */
  onCancel: () => void;
}

/**
 * Confirmation prompt for FE-SPEC §4's two-step `forceUnsafeRoot` flow.
 *
 * Backend rejects an add/update with 400 when the candidate rootDir's
 * `.fusion-backend.json` is missing, has the wrong `kind`, or sets
 * `supportsServiceManager: false`. The operator can opt in to the unsafe
 * path by resubmitting with `forceUnsafeRoot: true`, which makes the
 * supervisor accept the workplace but coerces `mode` to "standalone" —
 * the supervisor will manage existing PM2 processes for the workplace
 * but won't spawn new ones from an unverified rootDir.
 *
 * This modal opens on top of the form modal whenever the first submit
 * attempt hits that 400. Confirm → parent resubmits with the flag.
 * Cancel → parent re-surfaces the original error on the form modal.
 */
export default function ForceUnsafeRootModal({
  show,
  rootDir,
  serverMessage,
  onConfirm,
  onCancel,
}: ForceUnsafeRootModalProps) {
  return (
    <BaseModal
      show={show}
      handleShow={onCancel}
      size="md"
      title="Proceed with unverified root directory?"
      stackLevel={2}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="warning" onClick={onConfirm}>
            Proceed in standalone mode
          </Button>
        </>
      }
    >
      <div className="text-theme">
        <p className="mb-3">
          We can&apos;t confirm <span className="font-monospace text-warning">{rootDir}</span> is a
          Fusion BackendServer. The supervisor read{' '}
          <span className="font-monospace">.fusion-backend.json</span> and rejected it.
        </p>
        <p className="mb-3 text-theme-muted small">
          <span className="fw-semibold text-theme">Server message:</span> {serverMessage}
        </p>
        <p className="mb-3">
          You can proceed anyway. The supervisor will accept the workplace but force its mode to{' '}
          <span className="font-monospace text-warning">standalone</span>, meaning it will manage
          existing PM2 processes for this workplace (stop, disable, restart) but{' '}
          <span className="fw-semibold">will not start any new processes</span> from this path.
        </p>
        <p className="mb-0 text-theme-muted small">
          Cancel and edit the root directory if this isn&apos;t what you want.
        </p>
      </div>
    </BaseModal>
  );
}
